import {
  and,
  asc,
  bill_of_materials,
  bom_component,
  desc,
  eq,
  inventory_count,
  inventory_count_line,
  isNotNull,
  landed_cost,
  landed_cost_line,
  location,
  lot,
  manufacturing_order,
  type PowerSyncDrizzleDB,
  product,
  product_category,
  product_supplier,
  purchase_order,
  purchase_order_line,
  purchase_return,
  purchase_return_line,
  reorder_rule,
  stock_move,
  stock_move_line,
  stock_picking,
  stock_quant,
  uom,
  valuation_layer,
  vendor,
  warehouse,
} from "@stockpilot/powersync";
import {
  liveLocationFilters,
  liveProductFilters,
  liveStockPickingFilters,
  liveWarehouseFilters,
} from "../live/filters";

/** Productos VIVOS de una organización, ordenados por nombre (papelera fuera). */
export function getProductsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(product)
    .where(and(eq(product.organization_id, orgId), ...liveProductFilters()))
    .orderBy(product.name);
}

/** Un producto por id. */
export function getProductByIdQuery(db: PowerSyncDrizzleDB, id: string) {
  return db.select().from(product).where(eq(product.id, id));
}

/** Categorías de producto de una organización. */
export function getCategoriesQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(product_category)
    .where(eq(product_category.organization_id, orgId))
    .orderBy(product_category.name);
}

/** Unidades de medida de una organización. */
export function getUomsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db.select().from(uom).where(eq(uom.organization_id, orgId)).orderBy(uom.name);
}

/** Almacenes VIVOS de una organización (papelera fuera). */
export function getWarehousesQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(warehouse)
    .where(and(eq(warehouse.organization_id, orgId), ...liveWarehouseFilters()))
    .orderBy(warehouse.name);
}

/** Ubicaciones VIVAS de una organización (papelera fuera; cohorte almacén/padre). */
export function getLocationsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(location)
    .where(and(eq(location.organization_id, orgId), ...liveLocationFilters()))
    .orderBy(location.name);
}

/** Quants (on-hand por producto×ubicación) de una organización. */
export function getStockQuantsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db.select().from(stock_quant).where(eq(stock_quant.organization_id, orgId));
}

/** On-hand de un producto en una ubicación concreta. */
export function getQuantQuery(db: PowerSyncDrizzleDB, productId: string, locationId: string) {
  return db
    .select()
    .from(stock_quant)
    .where(and(eq(stock_quant.product_id, productId), eq(stock_quant.location_id, locationId)));
}

/** Un movimiento concreto (para registrar su contramovimiento). */
export function getMoveByIdQuery(db: PowerSyncDrizzleDB, id: string) {
  return db.select().from(stock_move).where(eq(stock_move.id, id));
}

/**
 * Mapa id→nombre de TODOS los productos visibles (papelera INCLUIDA y TODAS
 * las orgs sincronizadas): para pintar nombres en el feed de actividad aunque
 * el producto esté borrado o pertenezca a la org de otro miembro. El SQLite
 * local solo contiene filas accesibles (streams), así que no hace falta
 * re-filtrar por org.
 */
export function getProductNamesQuery(db: PowerSyncDrizzleDB) {
  return db.select({ id: product.id, name: product.name }).from(product);
}

/** Kardex: movimientos de un producto, más recientes primero. */
export function getMovesByProductQuery(db: PowerSyncDrizzleDB, productId: string) {
  return db
    .select()
    .from(stock_move)
    .where(eq(stock_move.product_id, productId))
    .orderBy(desc(stock_move.created_at));
}

// ─── OPERACIONES (Stockpilot F2) ────────────────────────────────────────────

/** Operaciones (pickings) VIVAS de una organización, más recientes primero. */
export function getPickingsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(stock_picking)
    .where(and(eq(stock_picking.organization_id, orgId), ...liveStockPickingFilters()))
    .orderBy(desc(stock_picking.created_at));
}

/** Una operación por id. */
export function getPickingByIdQuery(db: PowerSyncDrizzleDB, id: string) {
  return db.select().from(stock_picking).where(eq(stock_picking.id, id));
}

/** Líneas de una operación. */
export function getMoveLinesByPickingQuery(db: PowerSyncDrizzleDB, pickingId: string) {
  return db
    .select()
    .from(stock_move_line)
    .where(eq(stock_move_line.picking_id, pickingId))
    .orderBy(stock_move_line.created_at);
}

// ─── REABASTECIMIENTO (Stockpilot F2) ───────────────────────────────────────

/** Reglas de reabastecimiento (mín/máx) de una organización. */
export function getReorderRulesQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(reorder_rule)
    .where(eq(reorder_rule.organization_id, orgId))
    .orderBy(desc(reorder_rule.created_at));
}

// ─── COMPRAS (Stockpilot F3) ────────────────────────────────────────────────

/** Proveedores de una organización. */
export function getVendorsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db.select().from(vendor).where(eq(vendor.organization_id, orgId)).orderBy(vendor.name);
}

/** Precios proveedor-producto de una organización. */
export function getProductSuppliersQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db.select().from(product_supplier).where(eq(product_supplier.organization_id, orgId));
}

/** Precios proveedor de un producto concreto. */
export function getSuppliersByProductQuery(db: PowerSyncDrizzleDB, productId: string) {
  return db.select().from(product_supplier).where(eq(product_supplier.product_id, productId));
}

/** Órdenes de compra de una organización, más recientes primero. */
export function getPurchaseOrdersQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(purchase_order)
    .where(eq(purchase_order.organization_id, orgId))
    .orderBy(desc(purchase_order.created_at));
}

/** Una orden de compra por id. */
export function getPurchaseOrderByIdQuery(db: PowerSyncDrizzleDB, id: string) {
  return db.select().from(purchase_order).where(eq(purchase_order.id, id));
}

/** Líneas de una orden de compra. */
export function getPurchaseOrderLinesQuery(db: PowerSyncDrizzleDB, poId: string) {
  return db
    .select()
    .from(purchase_order_line)
    .where(eq(purchase_order_line.po_id, poId))
    .orderBy(asc(purchase_order_line.created_at));
}

// ─── VALORACIÓN (Stockpilot F3) ─────────────────────────────────────────────

/** Capas de valoración de una organización, en orden cronológico (AVCO). */
export function getValuationLayersQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(valuation_layer)
    .where(eq(valuation_layer.organization_id, orgId))
    .orderBy(asc(valuation_layer.created_at));
}

/** Capas de valoración de un producto, en orden cronológico. */
export function getValuationLayersByProductQuery(db: PowerSyncDrizzleDB, productId: string) {
  return db
    .select()
    .from(valuation_layer)
    .where(eq(valuation_layer.product_id, productId))
    .orderBy(asc(valuation_layer.created_at));
}

// ─── TRAZABILIDAD + CONTEOS (Stockpilot F4) ─────────────────────────────────

/** Lotes / series de una organización, por caducidad (FEFO) y luego nombre. */
export function getLotsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(lot)
    .where(eq(lot.organization_id, orgId))
    .orderBy(asc(lot.expiry_date), asc(lot.name));
}

/** Lotes de un producto concreto, por caducidad (FEFO). */
export function getLotsByProductQuery(db: PowerSyncDrizzleDB, productId: string) {
  return db
    .select()
    .from(lot)
    .where(eq(lot.product_id, productId))
    .orderBy(asc(lot.expiry_date), asc(lot.name));
}

/** On-hand por lote de un producto en una ubicación (quants con lot_id). */
export function getQuantsByProductLocationQuery(
  db: PowerSyncDrizzleDB,
  productId: string,
  locationId: string,
) {
  return db
    .select()
    .from(stock_quant)
    .where(and(eq(stock_quant.product_id, productId), eq(stock_quant.location_id, locationId)));
}

/** Documentos de conteo de una organización, más recientes primero. */
export function getInventoryCountsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(inventory_count)
    .where(eq(inventory_count.organization_id, orgId))
    .orderBy(desc(inventory_count.created_at));
}

/** Un documento de conteo por id. */
export function getInventoryCountByIdQuery(db: PowerSyncDrizzleDB, id: string) {
  return db.select().from(inventory_count).where(eq(inventory_count.id, id));
}

/** Líneas de un documento de conteo. */
export function getInventoryCountLinesQuery(db: PowerSyncDrizzleDB, countId: string) {
  return db
    .select()
    .from(inventory_count_line)
    .where(eq(inventory_count_line.count_id, countId))
    .orderBy(asc(inventory_count_line.created_at));
}

// ─── MANUFACTURA + LANDED COSTS + DEVOLUCIONES (Stockpilot F5) ────────────────

/** Listas de materiales (BoM) de una organización. */
export function getBomsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(bill_of_materials)
    .where(eq(bill_of_materials.organization_id, orgId))
    .orderBy(desc(bill_of_materials.created_at));
}

/** BoMs de un producto terminado concreto. */
export function getBomsByProductQuery(db: PowerSyncDrizzleDB, productId: string) {
  return db.select().from(bill_of_materials).where(eq(bill_of_materials.product_id, productId));
}

/** Componentes de una BoM. */
export function getBomComponentsQuery(db: PowerSyncDrizzleDB, bomId: string) {
  return db
    .select()
    .from(bom_component)
    .where(eq(bom_component.bom_id, bomId))
    .orderBy(asc(bom_component.created_at));
}

/** Órdenes de fabricación de una organización, más recientes primero. */
export function getManufacturingOrdersQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(manufacturing_order)
    .where(eq(manufacturing_order.organization_id, orgId))
    .orderBy(desc(manufacturing_order.created_at));
}

/** Una orden de fabricación por id. */
export function getManufacturingOrderByIdQuery(db: PowerSyncDrizzleDB, id: string) {
  return db.select().from(manufacturing_order).where(eq(manufacturing_order.id, id));
}

/** Landed costs de una organización, más recientes primero. */
export function getLandedCostsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(landed_cost)
    .where(eq(landed_cost.organization_id, orgId))
    .orderBy(desc(landed_cost.created_at));
}

/** Líneas (reparto) de un landed cost. */
export function getLandedCostLinesQuery(db: PowerSyncDrizzleDB, landedCostId: string) {
  return db
    .select()
    .from(landed_cost_line)
    .where(eq(landed_cost_line.landed_cost_id, landedCostId))
    .orderBy(asc(landed_cost_line.created_at));
}

/**
 * Recepciones (pickings tipo 'receipt') de una organización, para landed costs.
 *
 * BUG DE COHORTE corregido (invariante disperso): esta lectura era CRUDA (sin
 * `deleted_at`), así que una recepción enviada a la papelera seguía apareciendo
 * en el selector de landed costs (`apps/web/.../manufacturing.tsx`). Ahora
 * compone `liveStockPickingFilters()`, igual que `getPickingsQuery`.
 */
export function getReceiptsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(stock_picking)
    .where(
      and(
        eq(stock_picking.organization_id, orgId),
        eq(stock_picking.kind, "receipt"),
        ...liveStockPickingFilters(),
      ),
    )
    .orderBy(desc(stock_picking.created_at));
}

/** Devoluciones a proveedor de una organización, más recientes primero. */
export function getPurchaseReturnsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(purchase_return)
    .where(eq(purchase_return.organization_id, orgId))
    .orderBy(desc(purchase_return.created_at));
}

/** Líneas de una devolución a proveedor. */
export function getPurchaseReturnLinesQuery(db: PowerSyncDrizzleDB, returnId: string) {
  return db
    .select()
    .from(purchase_return_line)
    .where(eq(purchase_return_line.return_id, returnId))
    .orderBy(asc(purchase_return_line.created_at));
}

// ─── PAPELERA (ACTIVITY-CORE L2) ────────────────────────────────────────────
// Vistas de lo soft-borrado (deleted_at IS NOT NULL). Restaurar = limpiar el
// campo (mutations restore*). No existe purga: los moves son append-only y el
// write-path convierte cualquier DELETE duro en soft-delete.

/** Productos en la papelera, lo más reciente primero. */
export function getTrashedProductsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(product)
    .where(and(eq(product.organization_id, orgId), isNotNull(product.deleted_at)))
    .orderBy(desc(product.updated_at));
}

/** Almacenes en la papelera. */
export function getTrashedWarehousesQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(warehouse)
    .where(and(eq(warehouse.organization_id, orgId), isNotNull(warehouse.deleted_at)))
    .orderBy(desc(warehouse.updated_at));
}

/** Ubicaciones en la papelera. */
export function getTrashedLocationsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(location)
    .where(and(eq(location.organization_id, orgId), isNotNull(location.deleted_at)))
    .orderBy(desc(location.updated_at));
}

/** Operaciones en la papelera. */
export function getTrashedPickingsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(stock_picking)
    .where(and(eq(stock_picking.organization_id, orgId), isNotNull(stock_picking.deleted_at)))
    .orderBy(desc(stock_picking.updated_at));
}
